import React, { useState } from 'react';
import { AlertTriangle, CheckCheck } from 'lucide-react';
import { formatCurrency } from '../../utils/formatting';

// Resolve all price-difference lines at once (Tonya, 2026-10-06). Two steps: pick which price,
// then confirm with the order-total effect, since this can change dozens of lines in one click.
function BulkPriceResolve({ bulkPrice }) {
  const [pending, setPending] = useState(null); // 'po' | 'contract' | null
  const { count, regularCount, outliers, totalsAll, totalsRegular, onApply } = bulkPrice;

  if (pending) {
    const toAplus = pending === 'contract';
    // With outliers, the default applies to the ordinary lines only and leaves the outliers
    // for a per-line look; applying to everything is still one click away.
    const skipOutliers = outliers.length > 0 && regularCount > 0;
    const totals = skipOutliers ? totalsRegular : totalsAll;
    const lineCount = skipOutliers ? regularCount : count;
    const diff = totals.aplusTotal - totals.poTotal;
    const outlierNames = outliers.map(o => `line ${o.line} (${o.pct}%)`).join(', ');
    return (
      <div className="mt-3 pt-3 border-t border-amber-500/20 text-sm text-slate-300">
        {outliers.length > 0 && (
          <p className="mb-2 px-3 py-2 rounded-lg bg-amber-500/10 text-amber-300 text-xs">
            {outliers.length === 1 ? 'One line differs' : `${outliers.length} lines differ`} from A+ by much more
            than the rest: {outlierNames}. Check {outliers.length === 1 ? 'it' : 'them'} separately — it may be
            a wrong price in A+ or the wrong item.
          </p>
        )}
        <p className="mb-2">
          {toAplus ? 'Use the A+ price' : 'Keep the PO price'} on {lineCount} line{lineCount === 1 ? '' : 's'}?
          {' '}{lineCount === 1 ? 'It totals' : 'They total'} <span className="text-white font-medium">{formatCurrency(toAplus ? totals.aplusTotal : totals.poTotal)}</span>
          {toAplus && Math.abs(diff) >= 0.005 && (
            <span className="text-slate-400"> ({diff > 0 ? '+' : '−'}{formatCurrency(Math.abs(diff))} vs. the PO)</span>
          )}.
        </p>
        <div className="flex flex-wrap gap-2">
          <button
            onClick={() => { onApply(pending, !skipOutliers); setPending(null); }}
            className="px-3 py-1.5 bg-amber-500 hover:bg-amber-400 text-slate-900 font-medium rounded-lg text-xs transition-colors"
          >
            {skipOutliers
              ? `Apply to ${regularCount} line${regularCount === 1 ? '' : 's'}, skip ${outliers.length === 1 ? `line ${outliers[0].line}` : `${outliers.length} outliers`}`
              : `Apply to ${count} lines`}
          </button>
          {skipOutliers && (
            <button
              onClick={() => { onApply(pending, true); setPending(null); }}
              className="px-3 py-1.5 bg-slate-700 hover:bg-slate-600 text-white rounded-lg text-xs transition-colors"
            >
              Apply to all {count}
            </button>
          )}
          <button
            onClick={() => setPending(null)}
            className="px-3 py-1.5 bg-slate-700 hover:bg-slate-600 text-white rounded-lg text-xs transition-colors"
          >
            Cancel
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="mt-3 pt-3 border-t border-amber-500/20 flex flex-wrap items-center gap-2 text-sm">
      <span className="text-slate-300 flex items-center gap-1.5">
        <CheckCheck className="w-4 h-4 text-amber-400" />
        {count} lines have a PO vs. A+ price difference:
      </span>
      <button
        onClick={() => setPending('contract')}
        className="px-3 py-1.5 bg-slate-700 hover:bg-slate-600 text-white rounded-lg text-xs transition-colors"
      >
        Use A+ price on all
      </button>
      <button
        onClick={() => setPending('po')}
        className="px-3 py-1.5 bg-slate-700 hover:bg-slate-600 text-white rounded-lg text-xs transition-colors"
      >
        Keep PO price on all
      </button>
    </div>
  );
}

export default function ValidationIssues({ unresolvedIssues, bulkPrice }) {
  if (!unresolvedIssues || unresolvedIssues.length === 0) return null;

  const scrollToItem = (itemIndex) => {
    const el = document.getElementById(`line-item-${itemIndex}`);
    if (el) {
      el.scrollIntoView({ behavior: 'smooth', block: 'center' });
    }
  };

  return (
    <div id="validation-issues-panel" className="bg-amber-500/10 border border-amber-500/20 rounded-xl p-4">
      <h3 className="text-xs font-semibold text-amber-400 uppercase tracking-wider mb-2 flex items-center gap-2">
        <AlertTriangle className="w-3 h-3" />
        Items Need Attention
      </h3>
      <ul className="text-sm text-slate-300 space-y-1">
        {unresolvedIssues.map((issue, idx) => (
          <li key={idx} className="flex items-start gap-2">
            <span className="text-amber-400">•</span>
            {issue.itemIndex !== null ? (
              <span
                className="cursor-pointer hover:text-amber-400 hover:underline transition-colors"
                onClick={() => scrollToItem(issue.itemIndex)}
              >
                {issue.text}
              </span>
            ) : (
              <span>{issue.text}</span>
            )}
          </li>
        ))}
      </ul>
      {bulkPrice && <BulkPriceResolve bulkPrice={bulkPrice} />}
    </div>
  );
}
